import React, { useState } from "react";
import Footer from "./Footer";
import { CheckCircle } from "lucide-react";
import SafeHTML from "../../../components/SafeHTML";
import * as LucideIcons from "lucide-react";
import { Hourglass } from "react-loader-spinner";
import useStickyButton from "../hooks/useStickyButton";
import isMobileDevice from "../../../utils/isMobileDevice";
import DesktopQrModal from "../../../components/DesktopQrModal";
import SecureCamera from "../../../components/SecureCamera/SecureCamera";

// Import local icon assets
// Add all your icon images here
const iconAssets = {
  "clearIcon": require("../../../assets/images/plus/Clear_Photo.png"),
  "cornerIcon": require("../../../assets/images/plus/Corner_Photo.png"),
  "glareIcon": require("../../../assets/images/plus/Glare_Photo.png"),
};

const CheckQuality = (props) => {
  const {
    useOCRForData,
    ocrName,
    ocrIDNumber,
    isSubmitting,
    myKadSide,
    imageBlob,
    onRetake,
    onConfirm,
    onEditName,
    onEditIDNumber,
    uiConfig = {},
  } = props;

  const [isAcknowledged, setIsAcknowledged] = React.useState(false);
  const { buttonContainerRef, sentinelRef, isSticky } = useStickyButton();
  const [showDesktopNotice, setShowDesktopNotice] = useState(false);
  const [showCamera, setShowCamera] = useState(false);

  // Safe access
  const global = uiConfig?.Global || {};
  const checkQuality = uiConfig?.CheckQuality || {};
  const textColor = global?.text?.fontColor || "#6b7280"; // default gray-500
  const cardBgColor = global?.card?.backgroundColor || "#f3f4f6"; // gray-50
  const cardOpacity = global?.card?.opacity || "1"; // default opacity
  const buttonBgColor = global?.button?.backgroundColor || "#2563eb"; // blue-600
  const buttonTextColor = global?.button?.fontColor || "#ffffff"; // white
  const pageBgColor = global?.pageContainer?.backgroundColor || "#ffffff"; // white
  const showFooter = global?.Footer?.Show !== undefined ? global.Footer.Show : true;

  const handleRetake = () => {
    if (!isMobileDevice()) {
      setShowDesktopNotice(true);
      return;
    }

    setShowDesktopNotice(false);
    setShowCamera(true);
  };

  const getTitleHTML = () => {
    const titleConfig = checkQuality?.Title?.Text;
    if (titleConfig) {
      return titleConfig;
    }
    return `<h2 style="font-size: 1.25rem; font-weight: bold; margin-bottom: 0.5rem; text-align: center; color: ${textColor};">
      Check your photo quality
    </h2>`;
  };

  const getConfirmButtonConfig = () => {
    const globalButton = global?.button || {};
    const confirmButtonConfig = checkQuality?.ConfirmButton || {};
    
    // Build base style from global button config
    const globalStyle = globalButton?.Style || `width: 66.666667%; padding: 0.75rem 1rem; border-radius: 9999px; display: flex; align-items: center; justify-content: center; gap: 0.5rem; margin: 0 auto; background: ${buttonBgColor}; color: ${buttonTextColor}; transition: transform 0.15s ease-in-out; border: none; cursor: pointer;`;
    
    // Parse global style
    const parsedGlobalStyle = globalStyle.split(';').reduce((acc, style) => {
      const [key, value] = style.split(':').map(s => s.trim());
      if (key && value) {
        acc[key] = value;
      }
      return acc;
    }, {});
    
    // Parse and merge specific button style if it exists
    let finalStyle = { ...parsedGlobalStyle };
    if (confirmButtonConfig?.Style) {
      const parsedButtonStyle = confirmButtonConfig.Style.split(';').reduce((acc, style) => {
        const [key, value] = style.split(':').map(s => s.trim());
        if (key && value) {
          acc[key] = value;
        }
        return acc;
      }, {});
      finalStyle = { ...finalStyle, ...parsedButtonStyle };
    }
    
    // Convert back to CSS string
    const mergedStyleString = Object.entries(finalStyle)
      .map(([key, value]) => `${key}: ${value}`)
      .join('; ') + ';';
    
    return {
      text: confirmButtonConfig?.Text || globalButton?.Text || "Confirm",
      style: mergedStyleString,
      icon: {
        show: confirmButtonConfig?.Icon?.Show !== undefined ? confirmButtonConfig.Icon.Show : (globalButton?.Icon?.Show !== undefined ? globalButton.Icon.Show : true),
        position: confirmButtonConfig?.Icon?.Position || globalButton?.Icon?.Position || "end",
        name: confirmButtonConfig?.Icon?.Name || globalButton?.Icon?.Name || "CircleCheck",
        className: confirmButtonConfig?.Icon?.ClassName || globalButton?.Icon?.ClassName || "ml-2"
      }
    };
  };

  const getRetakeButtonConfig = () => {
    const globalButton = global?.button || {};
    const retakeButtonConfig = checkQuality?.RetakeButton || {};
    
    // Default retake button style (don't use global button style by default)
    const defaultStyle = `display: flex; align-items: center; justify-content: center; width: 100%; padding-top: 0.75rem; padding-bottom: 0.75rem; font-weight: 500; font-size: 1rem; margin-top: 0.5rem; transition: color 0.15s ease-in-out; color: #3b82f6; background: transparent; border: none; cursor: pointer;`;
    
    // Use retake-specific style if provided, otherwise use default (not global)
    const baseStyle = retakeButtonConfig?.Style || defaultStyle;
    
    // Parse base style
    const parsedBaseStyle = baseStyle.split(';').reduce((acc, style) => {
      const [key, value] = style.split(':').map(s => s.trim());
      if (key && value) {
        acc[key] = value;
      }
      return acc;
    }, {});
    
    // Retake button doesn't merge with global style, only uses its own config
    let finalStyle = { ...parsedBaseStyle };
    
    // Convert back to CSS string
    const mergedStyleString = Object.entries(finalStyle)
      .map(([key, value]) => `${key}: ${value}`)
      .join('; ') + ';';
    
    return {
      text: retakeButtonConfig?.Text || globalButton?.Text || "Retake",
      style: mergedStyleString,
      icon: {
        show: retakeButtonConfig?.Icon?.Show !== undefined ? retakeButtonConfig.Icon.Show : (globalButton?.Icon?.Show !== undefined ? globalButton.Icon.Show : true),
        position: retakeButtonConfig?.Icon?.Position || globalButton?.Icon?.Position || "end",
        name: retakeButtonConfig?.Icon?.Name || globalButton?.Icon?.Name || "Camera",
        className: retakeButtonConfig?.Icon?.ClassName || globalButton?.Icon?.ClassName || "ml-2"
      }
    };
  };

  const getChecklistConfig = () => {
    const checklistConfig = checkQuality?.Checklist;
    
    if (checklistConfig) {
      const defaultItems = [
        { text: "The photo is clear and sharp", icon: null },
        { text: "All corners of your MyKad are visible", icon: null },
        { text: "There is no glare or reflection", icon: null },
      ];
      
      if (useOCRForData) {
        defaultItems.push({ text: "The name and ID number are correct", icon: null });
      }
      
      return {
        title: checklistConfig?.Title?.Text || "PLEASE ENSURE:",
        items: checklistConfig?.Items && checklistConfig.Items.length > 0 ? checklistConfig.Items : defaultItems,
        itemSpacing: checklistConfig?.ItemSpacing || "0.5rem"
      };
    }
    
    // Default config
    const defaultItems = [
      { text: "The photo is clear and sharp", icon: null },
      { text: "All corners of your MyKad are visible", icon: null },
      { text: "There is no glare or reflection", icon: null },
    ];
    
    if (useOCRForData) {
      defaultItems.push({ text: "The name and ID number are correct", icon: null });
    }
    
    return {
      title: "PLEASE ENSURE:",
      items: defaultItems,
      itemSpacing: "0.5rem"
    };
  };

  const handleSecureCapture = ({ file }) => {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = async () => {
      onRetake(reader.result);
    };
  };

  return (
    <div
      style={{
        background: pageBgColor,
        padding: "1rem",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      <SecureCamera
        isOpen={showCamera}
        onClose={() => setShowCamera(false)}
        onCapture={handleSecureCapture}
        frameType="ID"
        title={`Retake MyKad (${myKadSide === "front" ? "front" : "back"})`}
        theme={{
          primaryColor: buttonBgColor,
          textColor: "#ffffff"
        }}
      />
      <SafeHTML html={getTitleHTML()} />
      <div>
        <div className="flex flex-col items-center mt-4 mb-4">
          <img
            src={URL.createObjectURL(imageBlob)}
            className="h-48 md:h-48 lg:h-48 w-auto rounded-lg shadow-md shadow-black"
          />
          <p
            style={{
              fontWeight: "bold",
              textAlign: "center",
              marginTop: "0.5rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: textColor,
            }}
          >
            MyKad {myKadSide === "front" ? "Front" : "Back"}
          </p>
        </div>

        {useOCRForData ? (
          <div
            style={{
              borderRadius: "0.75rem",
              padding: "1rem",
              marginBottom: "1.5rem",
              backgroundColor: cardBgColor,
              opacity: cardOpacity,
            }}
          >
            <ul className="space-y-2">
              <li className="flex items-center justify-center space-x-4">
                <span
                  style={{
                    color: textColor,
                  }}
                >
                  {ocrName}
                </span>
                {/* <button className="text-blue-500 hover:text-blue-600 transition-colors" onClick={onEditName}>
                  <Pencil size={18} />
                </button> */}
              </li>
              <li className="flex items-center justify-center space-x-4">
                <span
                  style={{
                    color: textColor,
                  }}
                >
                  {ocrIDNumber}
                </span>
                {/* <button className="text-blue-500 hover:text-blue-600 transition-colors" onClick={onEditIDNumber}>
                  <Pencil size={18} />
                </button> */}
              </li>
            </ul>
          </div>
        ) : null}

        {(() => {
          const checklistConfig = getChecklistConfig();
          return (
            <div
              style={{
                background: cardBgColor,
                opacity: cardOpacity,
                borderRadius: "0.75rem",
                padding: "1rem",
                marginBottom: "1.5rem",
              }}
            >
              <p
                style={{
                  fontSize: "0.875rem",
                  marginBottom: "0.75rem",
                  fontWeight: "500",
                  color: textColor,
                }}
              >
                {checklistConfig.title}
              </p>
              <div className="mt-4">
                <ul style={{ display: "flex", flexDirection: "column", gap: checklistConfig.itemSpacing, listStyle: "none", padding: 0, margin: 0 }}>
                  {checklistConfig.items.map((item, index) => {
                    // Determine icon source: local asset identifier or URL
                    const iconSrc = item.icon 
                      ? (iconAssets[item.icon] || item.icon) 
                      : null;
                    
                    return (
                      <li key={index} className="flex items-center space-x-2">
                        {iconSrc ? (
                          <img
                            src={iconSrc}
                            alt=""
                            style={{
                              width: "20px",
                              height: "20px",
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <CheckCircle className="text-green-500" />
                        )}
                        <span style={{ color: textColor }}>{item.text}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          );
        })()}
      </div>

      <div className="flex flex-1" />

      {/* Sentinel element for Intersection Observer */}
      <div ref={sentinelRef} style={{ height: "1px", visibility: "hidden" }} />

      {/* Action buttons area */}
      {isSubmitting ? (
        <div className="flex flex-col justify-center items-center mb-6">
          <Hourglass height={"32"} width={"32"} color="#1E40AF" />
          <p style={{ color: textColor, marginTop: "2rem" }}>
            Please wait while we upload your photo...
          </p>
        </div>
      ) : (
        <div 
          ref={buttonContainerRef}
          className="px-6 mb-6"
          style={{
            position: "relative",
            bottom: "auto",
            left: "auto",
            right: "auto",
            padding: "0 1.5rem",
            background: "transparent",
            zIndex: "auto",
            transition: "all 0.3s ease-in-out",
            pointerEvents: "auto",
          }}
        >
          {useOCRForData && (
            <div className="flex items-center justify-center mb-4">
              <input
                type="checkbox"
                id="acknowledgment"
                checked={isAcknowledged}
                onChange={(e) => setIsAcknowledged(e.target.checked)}
                className="mr-4 h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <label
                htmlFor="acknowledgment"
                style={{ color: textColor, fontWeight: "bold" }}
                className="text-sm"
              >
                I confirm that the extracted name and ID number are correct
              </label>
            </div>
          )}
          {(() => {
            const confirmBtnConfig = getConfirmButtonConfig();
            const ConfirmIconComponent = confirmBtnConfig.icon.show && LucideIcons[confirmBtnConfig.icon.name] ? LucideIcons[confirmBtnConfig.icon.name] : null;
            
            const confirmButtonStyle = confirmBtnConfig.style.split(';').reduce((acc, style) => {
              const [key, value] = style.split(':').map(s => s.trim());
              if (key && value) {
                const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                acc[camelKey] = value;
              }
              return acc;
            }, {});
            
            // Add opacity for disabled state
            if (useOCRForData && !isAcknowledged) {
              confirmButtonStyle.opacity = "0.5";
            }

            return (
              <button
                style={{ ...confirmButtonStyle, pointerEvents: 'auto' }}
                onClick={onConfirm}
                disabled={isSubmitting || (useOCRForData && !isAcknowledged)}
                onMouseDown={(e) => !e.currentTarget.disabled && (e.currentTarget.style.transform = 'scale(0.95)')}
                onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                onTouchStart={(e) => !e.currentTarget.disabled && (e.currentTarget.style.transform = 'scale(0.95)')}
                onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                {confirmBtnConfig.icon.position === "start" && ConfirmIconComponent && (
                  <ConfirmIconComponent className={confirmBtnConfig.icon.className} />
                )}
                <span>{confirmBtnConfig.text}</span>
                {confirmBtnConfig.icon.position === "end" && ConfirmIconComponent && (
                  <ConfirmIconComponent className={confirmBtnConfig.icon.className} />
                )}
              </button>
            );
          })()}
          {(() => {
            const retakeBtnConfig = getRetakeButtonConfig();
            const RetakeIconComponent = retakeBtnConfig.icon.show && LucideIcons[retakeBtnConfig.icon.name] ? LucideIcons[retakeBtnConfig.icon.name] : null;
            
            const retakeButtonStyle = retakeBtnConfig.style.split(';').reduce((acc, style) => {
              const [key, value] = style.split(':').map(s => s.trim());
              if (key && value) {
                const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                acc[camelKey] = value;
              }
              return acc;
            }, {});

            return (
              <button
                style={{ ...retakeButtonStyle, pointerEvents: 'auto' }}
                onClick={handleRetake}
                onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
                onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                onTouchStart={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
                onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                {retakeBtnConfig.icon.position === "start" && RetakeIconComponent && (
                  <RetakeIconComponent className={retakeBtnConfig.icon.className} />
                )}
                <span>{retakeBtnConfig.text}</span>
                {retakeBtnConfig.icon.position === "end" && RetakeIconComponent && (
                  <RetakeIconComponent className={retakeBtnConfig.icon.className} />
                )}
              </button>
            );
          })()}
        </div>
      )}

      <DesktopQrModal
        open={showDesktopNotice}
        onClose={() => setShowDesktopNotice(false)}
      />

      {showFooter ? (
        <Footer
          style={{
            fontSize: "0.75rem",
            color: textColor,
            marginTop: "1rem",
          }}
        />
      ) : (
        /* Minimal spacer to prevent sticky flickering when footer is hidden */
        <div style={{ height: "5px", flexShrink: 0 }} />
      )}
    </div>
  );
};

export default CheckQuality;
